import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Search, Package, Truck, CheckCircle, Clock, MapPin,
  Loader2, AlertCircle, ArrowRight, ShoppingBag
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ORDER_STEPS = [
  { key: 'pending', label: 'Order Placed', icon: ShoppingBag },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle },
  { key: 'processing', label: 'Processing', icon: Package },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: MapPin },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle },
];

const TrackOrder = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim()) {
      toast.error('Please enter an order number');
      return;
    }
    setLoading(true);
    setNotFound(false);
    setOrder(null);
    try {
      const res = await axios.get(`${API}/orders/track/${orderNumber.trim()}`);
      if (res.data.success) {
        setOrder(res.data.order);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error('Failed to track order');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStepIndex = (status) => {
    if (status === 'cancelled' || status === 'returned' || status === 'refunded') return -1;
    return ORDER_STEPS.findIndex(s => s.key === status);
  };

  const currentStep = order ? getStepIndex(order.status) : -1;
  const isCancelled = order && ['cancelled', 'returned', 'refunded', 'expired'].includes(order.status);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="track-order-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-green-700 via-green-600 to-emerald-700 text-white py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Truck className="h-8 w-8" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Track Your Order</h1>
          <p className="text-green-100 mb-8">Enter your order number to see real-time delivery status</p>

          <form onSubmit={handleTrack} className="max-w-md mx-auto flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-300" />
              <Input
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="Enter order number (e.g., ORD-...)"
                className="pl-10 h-12 bg-white/10 border-white/30 text-white placeholder:text-green-200 focus:bg-white/20"
                data-testid="track-input"
              />
            </div>
            <Button type="submit" disabled={loading} className="h-12 px-6 bg-white text-green-700 hover:bg-green-50 font-semibold" data-testid="track-submit">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Track'}
            </Button>
          </form>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Not Found */}
        {notFound && (
          <Card className="border-red-200">
            <CardContent className="py-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Order Not Found</h3>
              <p className="text-gray-500 text-sm">Please check your order number and try again.</p>
            </CardContent>
          </Card>
        )}

        {/* Order Details */}
        {order && (
          <div className="space-y-6" data-testid="track-result">
            {/* Order Header */}
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Order Number</p>
                    <h2 className="text-xl font-bold text-gray-900">{order.order_number}</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Placed on {new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Total</p>
                    <p className="text-2xl font-bold text-green-600">₦{(order.total_amount || 0).toLocaleString()}</p>
                    {order.estimated_delivery && (
                      <p className="text-xs text-gray-500 mt-1">
                        Est. Delivery: {new Date(order.estimated_delivery).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Progress Timeline */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-6">Delivery Progress</h3>

                {isCancelled ? (
                  <div className="text-center py-6">
                    <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
                    <p className="text-lg font-semibold text-red-600 capitalize">{order.status}</p>
                    <p className="text-sm text-gray-500 mt-1">This order has been {order.status}.</p>
                  </div>
                ) : (
                  <div className="relative">
                    {/* Progress Bar */}
                    <div className="hidden md:block absolute top-5 left-[5%] right-[5%] h-1 bg-gray-200 rounded">
                      <div
                        className="h-full bg-green-500 rounded transition-all duration-700"
                        style={{ width: `${currentStep >= 0 ? (currentStep / (ORDER_STEPS.length - 1)) * 100 : 0}%` }}
                      />
                    </div>

                    {/* Steps */}
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                      {ORDER_STEPS.map((step, index) => {
                        const isComplete = index <= currentStep;
                        const isCurrent = index === currentStep;
                        const StepIcon = step.icon;
                        return (
                          <div key={step.key} className="flex flex-col items-center text-center relative z-10">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all ${
                              isComplete ? 'bg-green-500 text-white shadow-lg shadow-green-200' :
                              'bg-gray-100 text-gray-400'
                            } ${isCurrent ? 'ring-4 ring-green-100 scale-110' : ''}`}>
                              <StepIcon className="h-4.5 w-4.5" />
                            </div>
                            <p className={`text-xs font-medium ${isComplete ? 'text-green-700' : 'text-gray-400'}`}>
                              {step.label}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Shipping Info */}
            {(order.tracking_number || order.shipping_provider) && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Truck className="h-5 w-5 text-green-600" /> Shipping Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {order.shipping_provider && (
                      <div>
                        <p className="text-gray-500">Shipping Provider</p>
                        <p className="font-semibold">{order.shipping_provider}</p>
                      </div>
                    )}
                    {order.tracking_number && (
                      <div>
                        <p className="text-gray-500">Tracking Number</p>
                        <p className="font-mono font-semibold">{order.tracking_number}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Status History */}
            {order.status_history && order.status_history.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Clock className="h-5 w-5 text-green-600" /> Status History
                  </h3>
                  <div className="space-y-3">
                    {[...order.status_history].reverse().map((entry, i) => (
                      <div key={`${entry.status}-${entry.changed_at}`} className="flex gap-3 items-start">
                        <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${i === 0 ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <div>
                          <p className="text-sm font-medium capitalize">{entry.status?.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(entry.changed_at).toLocaleString()} {entry.changed_by && `by ${entry.changed_by}`}
                          </p>
                          {entry.notes && <p className="text-xs text-gray-600 mt-0.5">{entry.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Order Items */}
            {order.items && order.items.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4">Order Items</h3>
                  <div className="space-y-2">
                    {order.items.map((item, i) => (
                      <div key={`${item.name}-${i}`} className="flex justify-between items-center py-2 border-b last:border-0">
                        <div>
                          <p className="font-medium text-sm">{item.name}</p>
                          <p className="text-xs text-gray-500">Qty: {item.quantity} {item.unit || ''}</p>
                        </div>
                        <p className="font-semibold text-sm">₦{(item.price * item.quantity).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Default state */}
        {!order && !notFound && !loading && (
          <div className="text-center py-16">
            <Package className="h-16 w-16 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-400">Enter your order number above to track your delivery</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackOrder;
